import { AddButton, Button, CloseButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { userProvider } from '../../../data/providers/userProvider';
import { IUser } from '../../../data/types';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import { createNewKey, IApiUrl } from '../../../data/writers/apiKeys';
import { Column } from '../../layout/Column';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { FormContainer } from '../../shared/form/FormContainer';
import { TextField } from '../../shared/form/TextField';
import { MultiTeamSelect } from '../../shared/form/selects/MultiTeamSelect';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';
import { Icon } from '../../shared/Icon';

/**
 * @description button to open the add api key popup
 */
const AddKeyPopup = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const openDialog = (): void =>
      openInnerPopup(
        <Popup onClose={closeInnerPopup(props.history)} />,
        props.history,
        'add'
      );

    return (
      <AddContainer>
        <AddButton onClick={openDialog} />
      </AddContainer>
    );
  }
);

const AddContainer = styled.div`
  padding: 0px 10px;
`;

const MigrationMessage = styled.div`
  padding: 12px 16px;
  background-color: rgba(255, 200, 0, 0.1);
  border: 1px solid rgba(255, 200, 0, 0.3);
  border-radius: 5px;
  color: #ffcc00;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;

  strong {
    display: block;
    margin-bottom: 4px;
  }
`;

const ScopeSelector = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
`;

const ScopeLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--color-text);
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;

  input[type='radio'] {
    margin-right: 6px;
    cursor: pointer;
  }
`;

const ValidationMessage = styled.div`
  padding: 8px 12px;
  background-color: rgba(255, 200, 0, 0.1);
  border-left: 3px solid rgba(255, 200, 0, 0.3);
  color: #ffcc00;
  font-size: 13px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  height: 40px;
`;

const ErrorMessage = styled.div`
  padding: 8px 12px;
  background-color: rgba(244, 67, 54, 0.1);
  border-left: 3px solid rgba(244, 67, 54, 0.5);
  color: #f44336;
  font-size: 13px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  height: 40px;
`;

const LargeTextField = styled(TextField)`
  font-size: 2em;
`;

export { AddKeyPopup };

interface IAddKeyPopupProps {
  onClose: () => void;
}

/**
 * @description add api key popup
 * @param props
 */
const Popup = (props: IAddKeyPopupProps): React.ReactElement => {
  const [saving, setSaving] = React.useState<boolean>(false);
  const [apiKey, setApiKey] = React.useState<string>();
  const [apiUrl, setApiUrl] = React.useState<string>('');
  const [apiTokenUrl, setApiTokenUrl] = React.useState<string>('');
  const [appName, setAppName] = React.useState<string>('');
  const [selectedTeamIds, setSelectedTeamIds] = React.useState<string[]>([]);
  const [tokenScope, setTokenScope] = React.useState<'team' | 'org'>('org');
  const [user, setUser] = React.useState<IUser>();
  const [userLoading, setUserLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string>();

  React.useEffect((): (() => void) => {
    const subscriber = userProvider().subscribe((mUser: IUser): void => {
      setUser(mUser);
      setUserLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, []);

  const isOrgAdmin = user?.claims?.orgAdmin === true;
  const hasV1NamedEndpoint = Boolean(user?.orgV1NamedEndpoint);

  /**
   * @description creates the new api key
   */
  const createKey = (): void => {
    setSaving(true);
    setError(undefined);
    createNewKey({ appName, teamIds: selectedTeamIds })
      .then(
        (result: { apiKey: string; apiUrl: IApiUrl }): void => {
          setSaving(false);
          setApiKey(result.apiKey);
          setApiTokenUrl(`${result.apiUrl.baseUrl}/${result.apiUrl.tokenPath}`);
          setApiUrl(`${result.apiUrl.baseUrl}/${result.apiUrl.endpoint}`);
        }
      )
      .catch((err: Error): void => {
        setSaving(false);
        console.error('Failed to create API key:', err);
        setError(
          err.message || 'Failed to create API key. Please try again.'
        );
      });
  };

  return (
    <InnerPopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Create API Key</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {(saving || userLoading) && <LinearLoading />}
          {!apiKey ? (
            <IntegrationForm
              name={appName}
              onChange={setAppName}
              selectedTeamIds={selectedTeamIds}
              onTeamSelectionChange={setSelectedTeamIds}
              tokenScope={tokenScope}
              onTokenScopeChange={setTokenScope}
              isOrgAdmin={isOrgAdmin}
              hasV1NamedEndpoint={hasV1NamedEndpoint}
              v1NamedEndpoint={user?.orgV1NamedEndpoint}
            />
          ) : (
            <GeneratedKey
              apiKey={apiKey}
              apiUrl={apiUrl}
              apiTokenUrl={apiTokenUrl}
            />
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            {!apiKey && error && (
              <ErrorMessage style={{ marginRight: '12px' }}>
                {error}
              </ErrorMessage>
            )}
            {!apiKey && !error && tokenScope === 'team' && selectedTeamIds.length === 0 && (
              <ValidationMessage style={{ marginRight: '12px' }}>
                Please select at least one team to create a team-scoped key
              </ValidationMessage>
            )}
            {!apiKey ? (
              <Button
                title='Create Key'
                type='submit'
                disabled={!appName || isEmpty(appName) || saving}
                onClick={createKey}
                icon='key'
                style={{ whiteSpace: 'nowrap' }}
              />
            ) : (
              <Button onClick={props.onClose} title='Close' />
            )}
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};

/**
 * @description displays the created key to the user
 * @param param0
 */
const GeneratedKey = ({
  apiKey,
  apiUrl,
  apiTokenUrl,
}: {
  apiKey: string;
  apiUrl: string;
  apiTokenUrl: string;
}): React.ReactElement => {
  return (
    <Column>
      <FormContainer>
        <ControlSpacer>
          <LargeTextField
            name='apiTokenUrl'
            readOnly
            value={apiTokenUrl}
            label='Token URL'
            type='text'
          />
        </ControlSpacer>
        <ControlSpacer>
          <LargeTextField
            name='apiUrl'
            readOnly
            value={apiUrl}
            label='API URL'
            type='text'
          />
        </ControlSpacer>
        <ControlSpacer>
          <LargeTextField
            name='name'
            readOnly
            value={apiKey}
            label='Key'
            type='text'
          />
        </ControlSpacer>
        <ControlSpacer>
          <strong>
            Please treat this key the same way you treat your username and
            password and store it somewhere safe. As soon as you close this
            dialog you will no longer be able to see this key.
          </strong>
        </ControlSpacer>
      </FormContainer>
    </Column>
  );
};

interface IIntegrationFormProps {
  name: string;
  onChange: (value: string) => void;
  selectedTeamIds: string[];
  onTeamSelectionChange: (teamIds: string[]) => void;
  tokenScope: 'team' | 'org';
  onTokenScopeChange: (scope: 'team' | 'org') => void;
  isOrgAdmin: boolean;
  hasV1NamedEndpoint: boolean;
  v1NamedEndpoint?: string;
}

/**
 * @description form for naming the api key
 * @param param0
 */
const IntegrationForm = ({
  name,
  onChange,
  selectedTeamIds,
  onTeamSelectionChange,
  tokenScope,
  onTokenScopeChange,
  isOrgAdmin,
  hasV1NamedEndpoint,
  v1NamedEndpoint,
}: IIntegrationFormProps): React.ReactElement => {
  const changeHandler = (value: string): void => onChange(value);

  const handleScopeChange = (scope: 'team' | 'org'): void => {
    onTokenScopeChange(scope);
    if (scope === 'org') {
      // Clear team selection when switching to org scope
      onTeamSelectionChange([]);
    }
  };

  return (
    <FormContainer>
      <ControlSpacer>
        <TextField
          name='name'
          value={name}
          label='Key Name'
          type='text'
          placeholder='Enter a name for the key...'
          onChange={changeHandler}
        />
      </ControlSpacer>
      {isOrgAdmin && (
        <>
          <ScopeSelector>
            <ScopeLabel>Key Access Scope</ScopeLabel>
            <RadioGroup>
              <RadioOption>
                <input
                  type='radio'
                  name='tokenScope'
                  value='org'
                  checked={tokenScope === 'org'}
                  onChange={(): void => handleScopeChange('org')}
                />
                Entire Organization
              </RadioOption>
              <RadioOption>
                <input
                  type='radio'
                  name='tokenScope'
                  value='team'
                  checked={tokenScope === 'team'}
                  onChange={(): void => handleScopeChange('team')}
                />
                Specific Team(s)
              </RadioOption>
            </RadioGroup>
          </ScopeSelector>
          {hasV1NamedEndpoint && tokenScope === 'team' && (
            <MigrationMessage role='note' aria-live='polite'>
              <Icon
                name='info'
                style={{ marginRight: '8px', verticalAlign: 'middle' }}
                aria-label='Information'
              />
              <strong>Endpoint Compatibility</strong>
              Team-scoped keys only work with <code>/api/v1</code>. Your existing
              org-scoped keys will continue to work with both <code>/api/v1</code>{' '}
              and <code>{`/api/${v1NamedEndpoint}`}</code>.
            </MigrationMessage>
          )}
          {tokenScope === 'team' && (
            <ControlSpacer>
              <MultiTeamSelect
                selectedTeamIds={selectedTeamIds}
                onChange={onTeamSelectionChange}
              />
            </ControlSpacer>
          )}
        </>
      )}
    </FormContainer>
  );
};
